
// src/forceLinksToBlank.js
// Force selected PATHS to open in a new tab, even if Volto/React intercepts clicks.
// Usage in src/index.js:
//   import { initForceLinksToBlank } from './forceLinksToBlank';
//   if (typeof window !== 'undefined') {
//     initForceLinksToBlank(['/what-we-do/products', '/contact-us']);
//   }

export function initForceLinksToBlank(forcedPaths = []) {
  if (typeof window === 'undefined') return;

  // Normalise a path: remove trailing slash except for root.
  const norm = (p) => {
    if (!p) return '/';
    try {
      const s = String(p).trim();
      if (s === '/') return '/';
      return s.replace(/\/+$/, '');
    } catch {
      return '/';
    }
  };

  const normalised = forcedPaths.map(norm);

  function pathIsForced(pathname) {
    const p = norm(pathname);
    // Match exact or subpaths. '/a' matches '/a' and '/a/...'
    return normalised.some((base) => p === base || p.startsWith(base + '/'));
  }

  function setForcedAttributes() {
    const anchors = document.querySelectorAll('a[href]');
    anchors.forEach((a) => {
      try {
        const url = new URL(a.href, window.location.origin);
        if (pathIsForced(url.pathname)) {
          a.setAttribute('target', '_blank');
          // Keep security best practice
          const rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
          if (!rel.includes('noopener')) rel.push('noopener');
          if (!rel.includes('noreferrer')) rel.push('noreferrer');
          a.setAttribute('rel', rel.join(' '));
        }
      } catch {
        /* ignore invalid URLs */
      }
    });
  }

  // Intercept clicks in the CAPTURE phase so we beat React Router / UniversalLink.
  function clickHandler(e) {
    if (e.defaultPrevented) return;
    // Left click only, no modifiers so we do not break native behaviours.
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;

    try {
      const url = new URL(a.href, window.location.origin);
      if (pathIsForced(url.pathname)) {
        // Ensure attributes are present for semantics
        a.setAttribute('target', '_blank');
        const rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
        if (!rel.includes('noopener')) rel.push('noopener');
        if (!rel.includes('noreferrer')) rel.push('noreferrer');
        a.setAttribute('rel', rel.join(' '));

        // Open explicitly to bypass SPA navigation
        window.open(url.href, '_blank', 'noopener');
        // Stop SPA handlers
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      }
    } catch {
      /* ignore */
    }
  }

  // Keyboard support: open in new tab when pressing Enter while focused on a forced link.
  function keydownHandler(e) {
    if (e.defaultPrevented) return;
    if (e.key !== 'Enter') return;
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;

    try {
      const url = new URL(a.href, window.location.origin);
      if (pathIsForced(url.pathname)) {
        a.setAttribute('target', '_blank');
        const rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
        if (!rel.includes('noopener')) rel.push('noopener');
        if (!rel.includes('noreferrer')) rel.push('noreferrer');
        a.setAttribute('rel', rel.join(' '));

        window.open(url.href, '_blank', 'noopener');
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      }
    } catch {
      /* ignore */
    }
  }

  // Initial pass for attributes (useful for right-click "open in new tab" UX)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setForcedAttributes, { once: true });
  } else {
    setForcedAttributes();
  }

  // Observe DOM changes to keep attributes updated
  const observer = new MutationObserver(setForcedAttributes);
  observer.observe(document.body, { childList: true, subtree: true });

  // Add delegated listeners in CAPTURE phase
  document.addEventListener('click', clickHandler, true);
  document.addEventListener('keydown', keydownHandler, true);

  // Return disposer
  return () => {
    observer.disconnect();
    document.removeEventListener('click', clickHandler, true);
    document.removeEventListener('keydown', keydownHandler, true);
  };
}
